import { type I18nLocale, t } from '@ardoq/i18n';
import { pluralize, pluralizeWithCount } from '@ardoq/common-helpers';

export type DatasetsListI18n = {
  title: string;
  addDataset: string;
  editDataset: string;
  viewpointBuilder: string;
  closeDataset: string;
  showDataset: string;
  hideDataset: string;
  collapseDataset: string;
  expandDataset: string;
  noComponentSelected: string;
  newConnectedData: string;
  /**
   * Renders both the dataset card title and the context component count aria
   * label. The key path is `contextComponent.*` for historical reasons.
   */
  componentCount: (count: number) => string;
  componentTypesSelected: (count: number) => string;
  descendantCount: (count: number) => string;
  descendantCountWithTypes: (count: number, typeCount: number) => string;
  changeContextAriaLabel: (label: string) => string;
  initFailed: string;
  createFailed: string;
};

export type TripleSelectorI18n = {
  addHop: string;
  addHopDisabledPopover: string;
  removeHop: string;
  removeHopDisabledTooltip: string;
  outgoing: string;
  incoming: string;
  loading: string;
  loaderAriaLabel: string;
  changeWildcard: string;
  changeWildcardDescription: string;
  startFromScratch: string;
  showAllDirectConnections: string;
  emptyStateLabel: string;
};

export type SelectContextI18n = {
  title: string;
  backButton: string;
  saveChanges: string;
  openComponents: (count: number) => string;
  loaderAriaLabel: string;
  searchMode: {
    search: string;
    advanced: string;
    title: string;
    searchNameLabel: string;
    searchNamePlaceholder: string;
    allComponents: string;
    componentTypeLabel: string;
    editRulesButton: string;
  };
  searchResults: {
    selectAll: string;
    resultsCount: (count: number) => string;
    /**
     * Non-plural in English and German, `_one`/`_other` in French. Each locale
     * declares the forms its grammar needs; `t` falls back to the bare key.
     */
    selectedCount: (count: number) => string;
    columnComponent: string;
    listAriaLabel: string;
  };
  rulesSummary: {
    ruleCount: (count: number) => string;
  };
  rulesModal: {
    title: string;
    description: string;
    cancel: string;
    seeResults: string;
  };
  emptyState: {
    noResults: {
      title: string;
      simpleDescription: string;
      advancedDescription: string;
    };
    simpleSearchHelp: {
      title: string;
      intro: string;
    };
    advancedSearchHelp: {
      title: string;
      message: string;
      addRulesButton: string;
    };
  };
};

export const getDatasetsListI18n = (
  i18nLocale: I18nLocale
): DatasetsListI18n => ({
  title: t(i18nLocale, 'canvas:datasetsTitle', 'Datasets'),
  addDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.addDataset',
    'Add dataset'
  ),
  editDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.editDataset',
    'Edit dataset'
  ),
  viewpointBuilder: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.viewpointBuilder',
    'Viewpoint builder'
  ),
  closeDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.closeDataset',
    'Close dataset'
  ),
  showDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.showDataset',
    'Show dataset'
  ),
  hideDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.hideDataset',
    'Hide dataset'
  ),
  collapseDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.collapseDataset',
    'Collapse dataset'
  ),
  expandDataset: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.expandDataset',
    'Expand dataset'
  ),
  noComponentSelected: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.contextComponent.noComponentSelected',
    'No component selected'
  ),
  newConnectedData: t(
    i18nLocale,
    'viewpointNavigator:entryLabel.newConnectedData',
    'New connected data'
  ),
  componentCount: (count: number) =>
    t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.contextComponent.componentCount',
      { count },
      pluralizeWithCount('component', count)
    ),
  componentTypesSelected: (count: number) =>
    t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.contextComponent.componentTypesSelected',
      { count },
      `${pluralizeWithCount('component type', count)} selected`
    ),
  descendantCount: (count: number) =>
    t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.contextComponent.descendantCount',
      { count },
      `+${pluralizeWithCount('descendant', count)}`
    ),
  descendantCountWithTypes: (count: number, typeCount: number) =>
    t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.contextComponent.descendantCountWithTypes',
      { count, typeCount },
      `+${pluralizeWithCount('descendant', count)} (${typeCount} types)`
    ),
  changeContextAriaLabel: (label: string) =>
    t(
      i18nLocale,
      'canvas:contextToolbarChangeContext',
      { label },
      `Change context: ${label}`
    ),
  initFailed: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.initFailed',
    'Could not load this dataset. Try refreshing the page.'
  ),
  createFailed: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.createFailed',
    'Could not create the dataset. Try again.'
  ),
});

export const getTripleSelectorI18n = (
  i18nLocale: I18nLocale
): TripleSelectorI18n => ({
  addHop: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.addHop',
    'Add reference'
  ),
  addHopDisabledPopover: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.addHopDisabledPopover',
    'You can’t add references because the context has different component types. Edit the dataset to select context components that are the same type.'
  ),
  removeHop: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.removeHop',
    'Remove reference'
  ),
  removeHopDisabledTooltip: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.removeHopDisabledTooltip',
    'This reference is part of a collapsed path. To remove it, delete the collapsed path in the viewpoint builder first.'
  ),
  outgoing: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.outgoing',
    'Outgoing'
  ),
  incoming: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.incoming',
    'Incoming'
  ),
  loading: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.loading',
    'Loading reference options'
  ),
  loaderAriaLabel: t(i18nLocale, 'loader:ariaLabel', 'loader'),
  changeWildcard: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.changeWildcard',
    'Change'
  ),
  changeWildcardDescription: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.changeWildcardDescription',
    'Select specific triples for a more tailored view.'
  ),
  startFromScratch: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.startFromScratch',
    'Start from scratch'
  ),
  showAllDirectConnections: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.showAllDirectConnections',
    'Show all direct connections'
  ),
  emptyStateLabel: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.tripleSelector.emptyStateLabel',
    'Add references to see connections'
  ),
});

export const getSelectContextI18n = (
  i18nLocale: I18nLocale
): SelectContextI18n => ({
  title: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.selectContext',
    'Select context'
  ),
  backButton: t(i18nLocale, 'canvas:backButtonLabel', 'Back'),
  saveChanges: t(
    i18nLocale,
    'canvas:improvedDatasetsPanel.saveChanges',
    'Save changes'
  ),
  openComponents: (count: number) =>
    t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.openComponents',
      { count },
      `Open ${pluralizeWithCount('component', count)}`
    ),
  loaderAriaLabel: t(i18nLocale, 'loader:ariaLabel', 'loader'),
  searchMode: {
    search: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.search',
      'Search'
    ),
    advanced: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.advanced',
      'Advanced'
    ),
    title: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.title',
      'Search context component'
    ),
    searchNameLabel: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.searchNameLabel',
      'Component name'
    ),
    searchNamePlaceholder: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.searchNamePlaceholder',
      'Search component name'
    ),
    allComponents: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.allComponents',
      'All component types'
    ),
    componentTypeLabel: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.componentTypeLabel',
      'Component type'
    ),
    editRulesButton: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchMode.editRulesButton',
      'Edit rules'
    ),
  },
  searchResults: {
    selectAll: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchResults.selectAll',
      'Select all'
    ),
    resultsCount: (count: number) =>
      t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.searchResults.resultsCount',
        { count },
        `${count} ${pluralize('result', count)}`
      ),
    selectedCount: (count: number) =>
      t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.searchResults.selectedCount',
        { count },
        '{{count}} selected'
      ),
    columnComponent: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchResults.column.component',
      'Component'
    ),
    listAriaLabel: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.searchResults.listAriaLabel',
      'Component search results'
    ),
  },
  rulesSummary: {
    ruleCount: (count: number) =>
      t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.advancedSearchRulesSummary.ruleCount',
        { count },
        `${count} ${pluralize('rule', count)} applied`
      ),
  },
  rulesModal: {
    title: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.advancedSearchRulesModal.title',
      'Add advanced search rules'
    ),
    description: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.advancedSearchRulesModal.description',
      'Add rules here to search for specific components'
    ),
    cancel: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.advancedSearchRulesModal.cancel',
      'Cancel'
    ),
    seeResults: t(
      i18nLocale,
      'canvas:improvedDatasetsPanel.advancedSearchRulesModal.seeResults',
      'See results'
    ),
  },
  emptyState: {
    noResults: {
      title: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.searchEmptyState.noResults.title',
        'No results found'
      ),
      simpleDescription: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.searchEmptyState.noResults.body',
        'Try searching for a different component or change your component type filter.'
      ),
      advancedDescription: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.advancedSearchEmptyState.noResults.body',
        'Try searching for a different component or change your rules.'
      ),
    },
    simpleSearchHelp: {
      title: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.searchEmptyState.title',
        'Search and select context components'
      ),
      intro: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.searchEmptyState.intro',
        'Your dataset is built on context components. Next, you can explore components connected through references.'
      ),
    },
    advancedSearchHelp: {
      title: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.advancedSearchEmptyState.title',
        'Search and select context components'
      ),
      message: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.advancedSearchEmptyState.message',
        'Add rules to find specific components for your dataset. The rules can be based on different component properties like who created it or its field values.'
      ),
      addRulesButton: t(
        i18nLocale,
        'canvas:improvedDatasetsPanel.advancedSearchEmptyState.addRulesButton',
        'Add rules'
      ),
    },
  },
});
